import { useMemo, useState } from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Button,
  CheckList,
  ErrorNotice,
  Field,
  PageHeading,
  Panel,
  SectionHeader,
  SelectInput,
  SkeletonTable,
  TextArea,
  TextInput,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  OUTREACH_PLAYBOOK_LIMITS,
  PLAYBOOK_QUERY_KEY,
  readPlaybook,
  savePlaybook,
  type ContextOption,
  type OutreachPlaybook,
  type PlaybookSituation,
} from '@/lib/outreach-playbook';
import { listMyTemplates, listTeamTemplates, OUTREACH_TEMPLATES_PATH, type OutreachTemplate } from '@/lib/outreach-templates';
import { OUTCOME_BOOKED, outcomeKeyLabel, readRepeatClients, REPEAT_CLIENTS_PATH, QUEUE_STATUS } from '@/lib/repeat-clients';

/** The situation choice that means "described in my own words". */
const DESCRIBED = '';
/** The template choice that lets the draft pick. */
const DRAFT_PICKS = '';

type Draft = Omit<OutreachPlaybook, 'updatedAt'>;

const EMPTY_SITUATION: PlaybookSituation = { outcome: OUTCOME_BOOKED, description: '', templateId: null, note: '' };

/**
 * Repeat clients → My reach-out playbook: the skill a rep's drafts follow.
 * Which template for which situation, what to bring into each email, what
 * they look for when reaching out, and their voice. A full page, not a side
 * form (Thomas, 2026-10-24).
 *
 * @returns The page.
 */
export function OutreachPlaybookPage(): React.ReactElement {
  const playbook = useQuery({ queryKey: PLAYBOOK_QUERY_KEY, queryFn: ({ signal }) => readPlaybook(signal) });
  const mine = useQuery({ queryKey: ['outreach-templates', 'mine'], queryFn: ({ signal }) => listMyTemplates(signal) });
  const team = useQuery({ queryKey: ['outreach-templates', 'team'], queryFn: ({ signal }) => listTeamTemplates(signal) });
  // The rep's own outcomes (Booked, each lost reason, other stages), for the situation choices.
  const outcomes = useQuery({
    queryKey: ['repeat-clients', 'outcome-options'],
    queryFn: ({ signal }) => readRepeatClients({ status: QUEUE_STATUS.toContact, outcomes: [], q: '', page: 1 }, signal),
    staleTime: 5 * 60_000,
  });

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="SALES"
        backTo={REPEAT_CLIENTS_PATH}
        title="My reach-out playbook"
        subtitle="How you reach out to past clients. Every email drafted for your clients follows it: the template for each situation, what to bring in, and how you sound."
      />
      {playbook.isError ? (
        <ErrorNotice title="Playbook unavailable" message={errorMessage(playbook.error)} />
      ) : playbook.isPending || mine.isPending || team.isPending ? (
        <SkeletonTable label="Loading your playbook…" />
      ) : (
        <PlaybookForm
          key={playbook.data.playbook.updatedAt ?? 'new'}
          initial={playbook.data.playbook}
          contextOptions={playbook.data.contextOptions}
          templates={[...(mine.data ?? []), ...(team.data?.templates ?? [])]}
          outcomeKeys={(outcomes.data?.outcomeOptions ?? []).map((option) => option.key)}
        />
      )}
    </section>
  );
}

interface PlaybookFormProps {
  readonly initial: OutreachPlaybook;
  readonly contextOptions: readonly ContextOption[];
  readonly templates: readonly OutreachTemplate[];
  readonly outcomeKeys: readonly string[];
}

function PlaybookForm({ initial, contextOptions, templates, outcomeKeys }: PlaybookFormProps): React.ReactElement {
  const queryClient = useQueryClient();
  const start: Draft = { voice: initial.voice, situations: initial.situations, context: initial.context, lookFor: initial.lookFor };
  const [draft, setDraft] = useState<Draft>(start);
  const dirty = JSON.stringify(draft) !== JSON.stringify(start);

  const save = useMutation({
    mutationFn: () => savePlaybook(draft),
    onSuccess: () => {
      toast.success('Playbook saved');
      void queryClient.invalidateQueries({ queryKey: PLAYBOOK_QUERY_KEY });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  // Situation choices: the rep's outcomes, plus any a saved rule names that no longer shows.
  const outcomeChoices = useMemo(() => {
    const keys = new Set([OUTCOME_BOOKED, ...outcomeKeys, ...draft.situations.map((s) => s.outcome).filter(Boolean)]);
    return [...keys].map((key) => ({ key, label: outcomeKeyLabel(key) }));
  }, [outcomeKeys, draft.situations]);

  const setSituation = (index: number, patch: Partial<PlaybookSituation>): void =>
    setDraft((d) => ({ ...d, situations: d.situations.map((s, i) => (i === index ? { ...s, ...patch } : s)) }));
  const incomplete = draft.situations.some((s) => s.outcome === DESCRIBED && s.description.trim() === '');

  return (
    <div className="space-y-6">
      <Panel className="p-5">
        <SectionHeader title="What you look for" description="Who you reach out to first and why: the signals that make a past client worth a call now." />
        <Field label="What you look for when reaching out" htmlFor="playbook-look-for" hint={`${draft.lookFor.length} / ${OUTREACH_PLAYBOOK_LIMITS.lookForChars.toLocaleString()}`}>
          <TextArea
            id="playbook-look-for"
            rows={4}
            maxLength={OUTREACH_PLAYBOOK_LIMITS.lookForChars}
            placeholder="Clients who booked two years running come first. Anyone whose event was in December gets a call by October."
            value={draft.lookFor}
            onChange={(event) => setDraft((d) => ({ ...d, lookFor: event.target.value }))}
          />
        </Field>
      </Panel>

      <Panel className="p-5">
        <SectionHeader
          title="Which template for which situation"
          description="Pick the template for each kind of client. When nothing matches, the draft picks the closest template."
          aside={
            <Link to={OUTREACH_TEMPLATES_PATH} className="text-portal-small font-bold text-brand hover:underline">
              My email templates
            </Link>
          }
        />
        {draft.situations.length === 0 ? (
          <p className="mb-4 text-portal-small text-portal-muted">No situations yet. Add one for each kind of client you write to differently.</p>
        ) : (
          <ol className="mb-4 space-y-3">
            {draft.situations.map((situation, index) => (
              <li key={index} className="grid gap-3 rounded-portal-card border border-portal-line p-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1.4fr)_auto] lg:items-end">
                <Field label="When the client" htmlFor={`situation-${index}`}>
                  <SelectInput
                    id={`situation-${index}`}
                    value={situation.outcome}
                    onChange={(event) => setSituation(index, { outcome: event.target.value })}
                  >
                    {outcomeChoices.map((choice) => (
                      <option key={choice.key} value={choice.key}>
                        {choice.label}
                      </option>
                    ))}
                    <option value={DESCRIBED}>Something else (describe it)</option>
                  </SelectInput>
                </Field>
                <Field label="Use template" htmlFor={`template-${index}`}>
                  <SelectInput
                    id={`template-${index}`}
                    value={situation.templateId ?? DRAFT_PICKS}
                    onChange={(event) => setSituation(index, { templateId: event.target.value || null })}
                  >
                    <option value={DRAFT_PICKS}>Let the draft pick</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                        {template.scope === 'team' ? ' (team)' : ''}
                      </option>
                    ))}
                  </SelectInput>
                </Field>
                <Field
                  label={situation.outcome === DESCRIBED ? 'The situation' : 'Anything to add'}
                  htmlFor={`note-${index}`}
                  required={situation.outcome === DESCRIBED}
                >
                  {situation.outcome === DESCRIBED ? (
                    <TextInput
                      id={`note-${index}`}
                      maxLength={OUTREACH_PLAYBOOK_LIMITS.situationChars}
                      placeholder="Had a poor program last time"
                      value={situation.description}
                      onChange={(event) => setSituation(index, { description: event.target.value })}
                    />
                  ) : (
                    <TextInput
                      id={`note-${index}`}
                      maxLength={OUTREACH_PLAYBOOK_LIMITS.noteChars}
                      placeholder="Mention the program they booked last time"
                      value={situation.note}
                      onChange={(event) => setSituation(index, { note: event.target.value })}
                    />
                  )}
                </Field>
                <Button
                  variant="secondary"
                  size="compact"
                  aria-label={`Remove situation ${index + 1}`}
                  onClick={() => setDraft((d) => ({ ...d, situations: d.situations.filter((_, i) => i !== index) }))}
                >
                  <Trash2Icon aria-hidden className="size-4" />
                </Button>
              </li>
            ))}
          </ol>
        )}
        <Button
          variant="secondary"
          disabled={draft.situations.length >= OUTREACH_PLAYBOOK_LIMITS.situations}
          onClick={() => setDraft((d) => ({ ...d, situations: [...d.situations, EMPTY_SITUATION] }))}
        >
          <PlusIcon aria-hidden className="size-4" /> Add a situation
        </Button>
      </Panel>

      <Panel className="p-5">
        <SectionHeader title="What to bring into each email" description="What the draft may mention about the client, from their history with us." />
        <CheckList
          label="What to bring into each email"
          options={contextOptions.map((option) => ({ key: option.key, label: option.label }))}
          values={new Set(draft.context)}
          onToggle={(key, checked) =>
            setDraft((d) => ({ ...d, context: checked ? [...d.context, key] : d.context.filter((k) => k !== key) }))
          }
        />
      </Panel>

      <Panel className="p-5">
        <SectionHeader title="Your voice" description="Tone, length, sign-off, and anything to always or never say." />
        <Field label="How you sound" htmlFor="playbook-voice" hint={`${draft.voice.length} / ${OUTREACH_PLAYBOOK_LIMITS.voiceChars.toLocaleString()}`}>
          <TextArea
            id="playbook-voice"
            rows={4}
            maxLength={OUTREACH_PLAYBOOK_LIMITS.voiceChars}
            placeholder={'Short and warm, three or four sentences. First names only. Never mention discounts. Sign off "Cheers, Sam".'}
            value={draft.voice}
            onChange={(event) => setDraft((d) => ({ ...d, voice: event.target.value }))}
          />
        </Field>
      </Panel>

      <div className="flex items-center justify-end gap-3">
        {incomplete ? <span className="text-portal-micro text-rose-700">Describe each “Something else” situation before saving.</span> : null}
        <Button variant="secondary" disabled={!dirty || save.isPending} onClick={() => setDraft(start)}>
          Discard changes
        </Button>
        <Button disabled={!dirty || incomplete || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save playbook'}
        </Button>
      </div>
    </div>
  );
}
